import { detectPopulatedSections } from './common/sectionUtils';

/**
 * Exports resume data to a JSON file
 * @param {Object} formData - The form data
 * @param {string} selectedLayout - The currently selected layout
 * @param {Object} currentColors - The actual resolved color codes currently being used
 * @param {string} selectedStyle - The currently selected visual style
 * @param {string} selectedBackground - The currently selected background effect
 * @param {Object} typographySettings - The typography settings
 * @param {string} selectedSpecialEffect - The currently selected special effect
 * @param {string} selectedBarStyle - The currently selected bar style
 * @param {Array} selectedSections - The selected sections
 * @param {string} filename - Optional custom filename
 */
export const exportResumeToJSON = (
  formData,
  selectedLayout,
  currentColors,
  selectedStyle,
  selectedBackground,
  typographySettings,
  selectedSpecialEffect,
  selectedBarStyle,
  selectedSections,
  filename = 'resume_data.json'
) => {
  // --- Typography: Ensure we have valid typography settings ---
  let cleanTypographySettings = {};
  if (typographySettings && typeof typographySettings === 'object') {
    const validTypographyKeys = ['cvTitle', 'sectionTitle', 'body', 'smallText'];
    validTypographyKeys.forEach(key => {
      if (typographySettings.hasOwnProperty(key) && typographySettings[key]) {
        cleanTypographySettings[key] = { ...typographySettings[key] };
      }
    });
  }

  // --- Design: Always save the actual resolved color codes ---
  let cleanColors = {};
  if (currentColors && typeof currentColors === 'object') {
    // Save all the actual color codes being used
    cleanColors = { ...currentColors };
  }
  
  // Ensure we have colors to export
  if (Object.keys(cleanColors).length === 0) {
    console.warn('ImportExportUtils: No colors found to export. This may indicate an issue with color resolution.');
  } else {
    console.log('ImportExportUtils: Exporting colors:', {
      colorCount: Object.keys(cleanColors).length,
      primaryColor: cleanColors.primary,
      textColor: cleanColors.text
    });
  }

  // --- Sections: Clean and save selected sections ---
  let cleanSelectedSections = [];
  if (Array.isArray(selectedSections)) {
    cleanSelectedSections = selectedSections.map(section => ({
      name: section.name,
      sectionKey: section.sectionKey,
      isArray: section.isArray,
      // Only save essential identifying information
    }));
  }

  // --- FormData: Ensure all required fields are present for backward compatibility ---
  let safeFormData = { ...formData };
  
  // Ensure enablePictureSpace field exists in personalInfo
  if (safeFormData.personalInfo && typeof safeFormData.personalInfo === 'object') {
    if (!('enablePictureSpace' in safeFormData.personalInfo)) {
      console.log('ImportExportUtils: Adding missing enablePictureSpace field to export data');
      safeFormData.personalInfo = {
        ...safeFormData.personalInfo,
        enablePictureSpace: true // Default value
      };
    }
  }

  // Create export data with all preferences including actual color values
  const exportData = {
    formData: safeFormData, // Use safe version with all required fields
    preferences: {
      layout: selectedLayout,
      colors: cleanColors, // Save actual color codes instead of design preference
      style: selectedStyle,
      background: selectedBackground,
      specialEffect: selectedSpecialEffect,
      barStyle: selectedBarStyle,
      typography: cleanTypographySettings
    },
    selectedSections: cleanSelectedSections,
  };

  console.log('ImportExportUtils: Exporting data with sections:', cleanSelectedSections.map(s => s.name));

  // Create a blob and download link
  const dataStr = JSON.stringify(exportData, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  
  // Clean up
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Imports resume data from a JSON file
 * @param {File} file - The file to import
 * @param {Function} setSelectedLayout - Function to update the layout
 * @param {Function} setFullCustomColorScheme - Function to set the entire custom color scheme object
 * @param {Function} setSelectedDesign - Function to update the design ID ('custom' or standard ID)
 * @param {Function} setSelectedStyle - Function to update the visual style
 * @param {Function} setSelectedBackground - Function to update the background effect
 * @param {Function} updateTypographySettings - Function to update typography
 * @param {Function} setSelectedSpecialEffect - Function to update the special effect
 * @param {Function} setSelectedBarStyle - Function to update the bar style
 * @param {Function} updateFormData - Function to update form data
 * @param {Function} setSelectedSections - Function to update selected sections
 * @param {Array} allSections - All available sections for mapping
 * @returns {Promise} A promise that resolves when import is complete
 */
export const importResumeFromJSON = (
  file,
  setSelectedLayout,
  setFullCustomColorScheme,
  setSelectedDesign,
  setSelectedStyle,
  setSelectedBackground,
  updateTypographySettings,
  setSelectedSpecialEffect,
  setSelectedBarStyle,
  updateFormData,
  setSelectedSections,
  allSections
) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file selected'));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        
        // Parse the file
        const fileContent = event.target.result;
        
        let importedData;
        try {
          importedData = JSON.parse(fileContent);
        } catch (parseError) {
          reject(new Error(`Invalid JSON format: ${parseError.message}`));
          return;
        }
                
        // Validate structure
        if (!importedData || typeof importedData !== 'object') {
          reject(new Error('Invalid import file format - not a valid JSON object'));
          return;
        }
        
        // Apply preferences first if they exist
        if (importedData.preferences && typeof importedData.preferences === 'object') {
          
          // Apply layout setting
          if (importedData.preferences.layout && typeof importedData.preferences.layout === 'string') {
            setSelectedLayout(importedData.preferences.layout);
          }
          
          // Handle both new format (colors) and legacy format (design)
          if (importedData.preferences.colors && typeof importedData.preferences.colors === 'object') {
            // New format: Apply actual color codes and set design to custom
            setFullCustomColorScheme(importedData.preferences.colors);
            setSelectedDesign('custom');
          } else if (importedData.preferences.design) {
            // Legacy format: Handle old design preference structure
            const designPref = importedData.preferences.design;
            if (typeof designPref === 'object') {
              // It's a custom scheme object
              setFullCustomColorScheme(designPref);
              setSelectedDesign('custom');
            } else if (typeof designPref === 'string') {
              // It's a standard design ID
              setSelectedDesign(designPref);
            }
          }
          
          // Apply style setting
          if (importedData.preferences.style && typeof importedData.preferences.style === 'string') {
            setSelectedStyle(importedData.preferences.style);
          }
          
          // Apply background setting
          if (importedData.preferences.background && typeof importedData.preferences.background === 'string') {
            setSelectedBackground(importedData.preferences.background);
          }

          // Apply special effect setting
          if (importedData.preferences.specialEffect && typeof importedData.preferences.specialEffect === 'string') {
            setSelectedSpecialEffect(importedData.preferences.specialEffect);
          }

          // Apply bar style setting
          if (importedData.preferences.barStyle && typeof importedData.preferences.barStyle === 'string') {
            setSelectedBarStyle(importedData.preferences.barStyle);
          }
          
          // Apply typography settings with validation
          if (importedData.preferences.typography && typeof importedData.preferences.typography === 'object') {
            console.log('ImportExportUtils: Applying typography settings:', importedData.preferences.typography);
            updateTypographySettings(importedData.preferences.typography);
          }
        }
        
        // Use a small timeout to allow preference contexts to potentially update UI if needed
        setTimeout(() => {
          let finalSelectedSections = [];

          // Update form data first
          if (importedData.formData && typeof importedData.formData === 'object') {
            updateFormData(null, importedData.formData);
          } else {
             // Even if formData is missing, we might have selectedSections defined
             console.warn("Imported JSON missing 'formData' object.");
          }

          // UPDATED: Always auto-detect sections based on formData content
          // This ensures cards automatically open when there's data, regardless of what was manually selected before
          if (importedData.formData && allSections) {
            finalSelectedSections = detectPopulatedSections(importedData.formData, allSections);
          } 
          
          // Fallback: Try to use explicit selectedSections from JSON if auto-detection fails
          else if (Array.isArray(importedData.selectedSections)) {
            console.log("Fallback: Using sections from 'selectedSections' array in JSON.");
            finalSelectedSections = importedData.selectedSections
              .map(importedSection => {
                // Find the full section object from allSections based on name
                const sectionName = typeof importedSection === 'string' ? importedSection : importedSection?.name;
                const foundSection = allSections.find(s => s.name === sectionName);
                if (foundSection) {
                  return { ...foundSection, expanded: true }; // Ensure cards open
                }
                return null;
              })
              .filter(Boolean); // Filter out any sections not found
          } else {
             console.warn("Could not determine selected sections from imported JSON.");
          }

          // Update the selected sections state with the fully mapped objects
          console.log("Final selected sections:", finalSelectedSections);
          setSelectedSections(finalSelectedSections);

          resolve('Import successful');
        }, 100);
      } catch (error) {
        console.error("Error processing imported JSON:", error);
        reject(new Error(`Error processing import file: ${error.message}`));
      }
    };
    
    reader.onerror = (error) => {
      reject(new Error('Error reading the file'));
    };
    
    reader.readAsText(file);
  });
};

/**
 * Enhanced JSON export with consistent user feedback
 *
 * @param {Object} formData - The form data
 * @param {string} selectedLayout - The currently selected layout
 * @param {Object} currentColors - The actual resolved color codes currently being used
 * @param {string} selectedStyle - The currently selected visual style
 * @param {string} selectedBackground - The currently selected background effect
 * @param {Object} typographySettings - The typography settings
 * @param {string} selectedSpecialEffect - The currently selected special effect
 * @param {string} selectedBarStyle - The currently selected bar style
 * @param {Array} selectedSections - The selected sections
 * @param {Function} onComplete - Callback after completion
 * @returns {Promise<boolean>} - Whether the export was successful
 */
export const enhancedExportJSON = (
  formData,
  selectedLayout,
  currentColors,
  selectedStyle,
  selectedBackground,
  typographySettings,
  selectedSpecialEffect,
  selectedBarStyle,
  selectedSections,
  onComplete = null
) => {
  try {
    // Use the existing export function
    exportResumeToJSON(
      formData,
      selectedLayout,
      currentColors,
      selectedStyle,
      selectedBackground,
      typographySettings,
      selectedSpecialEffect,
      selectedBarStyle,
      selectedSections
    );
    
    // Optional: Add standard success feedback
    // alert('Resume data exported successfully!');
    
    // Run completion callback if provided
    if (onComplete && typeof onComplete === 'function') {
      onComplete();
    }
    
    return true;
  } catch (error) {
    console.error('Failed to export data:', error); // Log the error for debugging
    alert('Failed to export data: ' + error.message);
    return false;
  }
};

/**
 * Enhanced JSON import with consistent user feedback and error handling
 *
 * @param {File} file - The file to import
 * @param {Function} setSelectedLayout - Function to update the layout
 * @param {Function} setFullCustomColorScheme - Function to set the entire custom color scheme object
 * @param {Function} setSelectedDesign - Function to update the design ID
 * @param {Function} setSelectedStyle - Function to update the visual style
 * @param {Function} setSelectedBackground - Function to update the background effect
 * @param {Function} updateTypographySettings - Function to update typography
 * @param {Function} setSelectedSpecialEffect - Function to update the special effect
 * @param {Function} setSelectedBarStyle - Function to update the bar style
 * @param {Function} updateFormData - Function to update form data
 * @param {Function} setSelectedSections - Function to update selected sections
 * @param {Array} allSections - All available sections for mapping
 * @param {Object} options - Additional options
 * @param {boolean} options.showSuccessMessage - Whether to show success alert
 * @param {Function} options.onComplete - Callback after completion
 * @returns {Promise<boolean>} - Whether the import was successful
 */
export const enhancedImportJSON = async (
  file,
  setSelectedLayout,
  setFullCustomColorScheme,
  setSelectedDesign,
  setSelectedStyle,
  setSelectedBackground,
  updateTypographySettings,
  setSelectedSpecialEffect,
  setSelectedBarStyle,
  updateFormData,
  setSelectedSections,
  allSections,
  { showSuccessMessage = true, onComplete = null } = {}
) => {
  if (!file) {
    alert('No file selected');
    return false;
  }
  
  try {
    await importResumeFromJSON(
      file,
      setSelectedLayout,
      setFullCustomColorScheme,
      setSelectedDesign,
      setSelectedStyle,
      setSelectedBackground,
      updateTypographySettings,
      setSelectedSpecialEffect,
      setSelectedBarStyle,
      updateFormData,
      setSelectedSections,
      allSections
    );
    
    if (showSuccessMessage) {
      alert('Resume data imported successfully!');
    }
    
    if (onComplete && typeof onComplete === 'function') {
      onComplete();
    }
    
    return true;
  } catch (error) {
    alert('Failed to import data: ' + error.message);
    return false;
  }
}; 